import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Clock,
  UserCheck,
  MapPin,
  Truck,
  ShieldPlus,
  PackageCheck,
  Boxes,
  Route,
  Package,
  Timer,
  Handshake,
  Award,
} from "lucide-react";
import SEO from "../components/SEO";
import SectionReveal from "../components/SectionReveal";
import QuickQuoteForm from "../components/forms/QuickQuoteForm";
import LocalBusinessSchema from "../components/LocalBusinessSchema";
import { SEO as META, SERVICES, TRUST_SIGNALS, PROMISES } from "../content";

const ICONS = {
  ShieldCheck,
  Clock,
  UserCheck,
  MapPin,
  Truck,
  ShieldPlus,
  PackageCheck,
  Boxes,
  Route,
  Package,
  Timer,
  Handshake,
  Award,
};

function Icon({ name, className }) {
  const C = ICONS[name] || ShieldCheck;
  return <C className={className} aria-hidden="true" />;
}

export default function Home() {
  return (
    <>
      <SEO {...META.home} />
      <LocalBusinessSchema />

      {/* HERO */}
      <section
        className="relative isolate flex min-h-[88vh] items-center overflow-hidden"
        data-testid="home-hero"
      >
        <div className="absolute inset-0 -z-10">
          <img
            src="/images/hero-home.jpg"
            alt=""
            className="h-full w-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-scrim" />
        </div>
        <div className="container-pad py-24 md:py-28 lg:py-32">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
            <SectionReveal yOffset={28} className="lg:col-span-7">
              <div>
                <span className="inline-flex items-center gap-2 rounded-md border border-white/25 bg-white/5 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-white/90 backdrop-blur-sm">
                  <span className="h-1.5 w-1.5 rounded-full bg-ridge-amber" aria-hidden="true" />
                  Milton, Ontario · Serving the GTA
                </span>
                <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-7xl">
                  Precision Logistics.
                  <br />
                  <span className="text-ridge-amber">Elevated Standards.</span>
                </h1>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/85 md:text-lg">
                  Milton's premium courier and logistics partner — delivering with
                  care, speed, and accountability across the Greater Toronto Area.
                </p>
                <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <Link
                    to="/quote"
                    className="btn-accent"
                    data-testid="home-hero-cta-quote"
                  >
                    Request a Quote
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link
                    to="/services"
                    className="btn-secondary-light"
                    data-testid="home-hero-cta-services"
                  >
                    Our Services
                  </Link>
                </div>
              </div>
            </SectionReveal>

            <SectionReveal className="lg:col-span-5" delay={0.1}>
              <QuickQuoteForm />
            </SectionReveal>
          </div>
        </div>

        {/* Bottom hairline accent */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-white/15" />
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="section-pad bg-white" data-testid="home-services-section">
        <div className="container-pad">
          <SectionReveal>
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
              <div className="max-w-2xl">
                <span className="overline">What we deliver</span>
                <h2 className="h2 mt-3">Logistics, engineered around your day.</h2>
              </div>
              <p className="body-muted max-w-md">
                Five focused services. One accountable partner. Built for businesses
                that can't afford a bad handoff.
              </p>
            </div>
          </SectionReveal>

          <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-md border border-ridge-border bg-ridge-border md:grid-cols-2 lg:grid-cols-5">
            {SERVICES.map((service, idx) => (
              <SectionReveal key={service.slug} delay={idx * 0.05}>
                <Link
                  to={`/services#${service.slug}`}
                  data-testid={`home-service-card-${service.slug}`}
                  className="group flex h-full flex-col gap-4 bg-white p-7 transition-colors duration-300 hover:bg-ridge-stone md:p-8"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-md bg-ridge-charcoal text-white transition-colors duration-300 group-hover:bg-ridge-amber">
                    <Icon name={service.iconName} className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-lg font-semibold leading-snug text-ridge-charcoal">
                    {service.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-ridge-text">
                    {service.overviewBody}
                  </p>
                  <span className="mt-auto inline-flex items-center gap-1.5 pt-2 text-xs font-semibold uppercase tracking-[0.16em] text-ridge-amber transition-colors group-hover:text-ridge-amber-dark">
                    Learn more
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST SIGNALS */}
      <section
        className="section-pad bg-ridge-stone"
        data-testid="home-trust-section"
      >
        <div className="container-pad">
          <SectionReveal>
            <div className="max-w-3xl">
              <span className="overline">Why businesses choose Ridgehawk</span>
              <h2 className="h2 mt-3">
                Founder-led. Safety-certified. Quietly relentless.
              </h2>
            </div>
          </SectionReveal>

          <div className="mt-14 grid grid-cols-1 gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
            {TRUST_SIGNALS.map((item, idx) => (
              <SectionReveal key={item.title} delay={idx * 0.06}>
                <div data-testid={`trust-signal-${idx}`}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-md border border-ridge-charcoal/15 bg-white">
                    <Icon name={item.iconName} className="h-5 w-5 text-ridge-amber" />
                  </div>
                  <span className="accent-rule mt-6 block" />
                  <h3 className="font-display mt-5 text-lg font-semibold text-ridge-charcoal">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-ridge-text">
                    {item.body}
                  </p>
                </div>
              </SectionReveal>
            ))}
          </div>
        </div>
      </section>

      {/* OUR PROMISE */}
      <section className="section-pad bg-white" data-testid="home-promise-section">
        <div className="container-pad">
          <SectionReveal>
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
              <div className="lg:col-span-5">
                <span className="overline">Our promise</span>
                <h2 className="h2 mt-3">
                  Logistics built on a simple belief.
                </h2>
                <p className="body-base mt-6 text-ridge-muted">
                  Every package handled with care. Every deadline respected. Every
                  client treated as a partner. Ridgehawk Logistics was built on a
                  simple belief: logistics should be personal, precise, and premium.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:col-span-7">
                {PROMISES.map((p, idx) => (
                  <SectionReveal key={p.title} delay={idx * 0.06}>
                    <div
                      className="card-soft group h-full hover:-translate-y-0.5 hover:border-ridge-charcoal/25"
                      data-testid={`promise-card-${idx}`}
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-ridge-charcoal text-white transition-colors duration-300 group-hover:bg-ridge-amber">
                        <Icon name={p.iconName} className="h-4.5 w-4.5" />
                      </div>
                      <h3 className="font-display mt-5 text-base font-semibold leading-snug text-ridge-charcoal">
                        {p.title}
                      </h3>
                      <p className="mt-3 text-sm leading-relaxed text-ridge-text">
                        {p.body}
                      </p>
                    </div>
                  </SectionReveal>
                ))}
              </div>
            </div>
          </SectionReveal>
        </div>
      </section>

      {/* CTA BANNER */}
      <section
        className="relative overflow-hidden bg-ridge-amber"
        data-testid="home-cta-banner"
      >
        <div className="container-pad py-16 md:py-20 lg:py-24">
          <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-[0.22em] text-white/80">
                Ready to move?
              </span>
              <h2 className="font-display mt-3 text-3xl font-bold leading-[1.1] tracking-tight text-white md:text-4xl lg:text-5xl">
                Get a custom quote for your delivery needs — local, regional, or
                dedicated routes.
              </h2>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to="/quote"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-ridge-charcoal px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.08em] text-white transition-colors duration-200 hover:bg-black"
                data-testid="home-cta-banner-quote"
              >
                Get a Free Quote
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-md border-2 border-white px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.08em] text-white transition-colors duration-200 hover:bg-white hover:text-ridge-charcoal"
                data-testid="home-cta-banner-contact"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute inset-y-0 right-0 hidden w-1/3 bg-[linear-gradient(105deg,transparent_0%,rgba(0,0,0,0.06)_60%,rgba(0,0,0,0.12)_100%)] lg:block" />
      </section>
    </>
  );
}
